import { computed, onMounted, ref, watch } from 'vue';
import { debounce, uniq } from 'lodash';
import { useI18n } from '@/composables';
import { $emitter } from '@/services/emitter';
import {
  getDictionaryApiRequest,
  postValidateDictionaryApiRequest,
} from '@/services/api/dictionary-api';
import { useStore } from '@/store';

type DictionaryItem = {
  name: string;
  code?: string;
};

type FlowDictionaryConditionEntry = {
  predicate: string;
  includeUnknown?: boolean;
  values: string[] | DictionaryItem[];
};

type FlowConditions = Record<string, FlowDictionaryConditionEntry>;

type FlowDictionaryConditionProps = {
  conditions: FlowConditions;
  api: string;
  name: string;
  allowCustom?: boolean;
};

export const useFlowDictionaryCondition = (props: FlowDictionaryConditionProps) => {
  const store = useStore();
  const { t } = useI18n();
  const search = ref('');
  const predicate = ref('EQUALS');
  const items = ref<DictionaryItem[]>([]);
  const loading = ref(false);
  const includeUnknown = ref(false);
  const selected = ref<Array<string | DictionaryItem>>([]);

  const isNameCountry = computed(() => props.name === 'country');

  const loadRequest = () => {
    if (isNameCountry.value) {
      return Promise.resolve(store.countryList as DictionaryItem[]);
    }

    return getDictionaryApiRequest(props.api, { limit: 100, name: search.value }) as Promise<DictionaryItem[]>;
  };

  const load = (updateItems = true) =>
    loadRequest().then((loadedItems) => {
      if (updateItems) {
        items.value = uniq([...loadedItems, ...items.value]);
      }
      return loadedItems;
    });

  const debouncedSearch = debounce(() => {
    load();
  }, 500);

  const setCondition = () => {
    let value = selected.value;
    const key = props.name === 'country' ? 'code' : 'name';

    if (isNameCountry.value) {
      value = (value as DictionaryItem[]).map((item) => item[key as keyof DictionaryItem] as string);
    }

    if (!props.conditions[props.name]) {
      props.conditions[props.name] = {
        predicate: 'EQUALS',
        includeUnknown: includeUnknown.value,
        values: [],
      };
    }

    props.conditions[props.name].values = value as string[];
  };

  const onBlur = (event: FocusEvent, payload: { value: string[] }) => {
    $emitter.emit('loading-add-dictionary', true);

    postValidateDictionaryApiRequest(props.api, payload.value)
      .then((response) => {
        const validatedItems = response as DictionaryItem[];
        if (validatedItems.length !== payload.value.length) {
          $emitter.emit(
            'toast-warning',
            t(
              `Some of the elements can't be added due to the misspelling. Please check the correct values using 'From the list' settings of this rule.`,
            ),
          );
        }

        items.value = uniq([...items.value, ...validatedItems]);
        if (isNameCountry.value) {
          selected.value = validatedItems;
        } else {
          selected.value = validatedItems.map((item) => item.name);
        }

        setCondition();

        const relatedTarget = (event as FocusEvent & { relatedTarget?: HTMLElement }).relatedTarget;
        if (
          relatedTarget
          && relatedTarget.type === 'button'
          && relatedTarget.dataset.action === 'save'
        ) {
          setTimeout(() => relatedTarget.click());
        }
      })
      .finally(() => {
        $emitter.emit('loading-add-dictionary', false);
      });
  };

  watch(selected, () => {
    setCondition();
  });

  watch(predicate, (value) => {
    props.conditions[props.name].predicate = value;
  });

  watch(includeUnknown, (value) => {
    props.conditions[props.name].includeUnknown = value;
  });

  watch(search, () => {
    debouncedSearch();
  });

  onMounted(() => {
    if (props.conditions[props.name]) {
      includeUnknown.value = Boolean(props.conditions[props.name].includeUnknown);
      predicate.value = props.conditions[props.name].predicate || 'EQUALS';
    } else {
      props.conditions[props.name] = {
        predicate: predicate.value,
        includeUnknown: includeUnknown.value,
        values: selected.value as string[],
      };
    }

    loading.value = true;
    load(false).then((loadedItems) => {
      let values = props.conditions[props.name].values || [];

      if (isNameCountry.value) {
        values = (values as string[]).map(
          (code) => loadedItems.find((item) => item.code === code),
        ).filter(Boolean) as DictionaryItem[];
      }

      selected.value = values as Array<string | DictionaryItem>;
      items.value = uniq([...loadedItems, ...(selected.value as DictionaryItem[])]);

      loading.value = false;
    });
  });

  return {
    t,
    search,
    predicate,
    items,
    loading,
    includeUnknown,
    selected,
    isNameCountry,
    onBlur,
  };
};
